import React, { ChangeEvent, Component } from "react";
import {
    createStyles,
    Paper,
    SnackbarContent,
    TextField,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { Info } from "@material-ui/icons";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { RedirectTo } from "../../helpers/RedirectTo";
import Constants from "../../models/utility/Constants";
import { LoadState } from "../../models/utility/LoadState";
import { IAppDispatch, IAppState } from "../../store";
import * as AppConfigStore from "../../store/AppConfigStore";
import * as UserStore from "../../store/UserStore";
import LoaderButton from "../shared/LoaderButton";
import { AppInsightsLogger } from "../shared/logging/AppInsightsLogger";
import SectionBanner from "../shared/SectionBanner";

type ForgotPasswordProps = UserStore.IUserStoreState &
    typeof UserStore.actionCreators &
    AppConfigStore.IAppConfigStoreState &
    WithStyles<typeof styles>;
interface IForgotPasswordState {
    email: string;
    errorMessages: string[];
}

const styles = (theme: Theme) =>
    createStyles({
        bodyText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.common.white,
            lineHeight: 1.5,
            marginBottom: theme.spacing(3)
        },
        button: { marginTop: theme.spacing(3) },
        errorMessage: { textAlign: "left" },
        form: {
            maxWidth: 650,
            padding: theme.spacing(8),
            width: "100%"
        },
        formFieldTop: { marginTop: 0 },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#231F20"
        },
        successContainer: {
            backgroundColor: theme.palette.success.main,
            maxWidth: "100%",
            verticalAlign: "top",
            width: "100%"
        },
        successIcon: {
            color: theme.palette.success.contrastText,
            marginRight: theme.spacing(),
            opacity: 0.9
        },
        successMessage: {
            alignItems: "flex-start",
            color: theme.palette.success.contrastText,
            display: "flex"
        },
        input: {
            color: "white"
        },
        floatingLabel: {
            color: "#CCCCCC"
        }
    });

class ForgotPassword extends Component<
    ForgotPasswordProps,
    IForgotPasswordState
> {
    constructor(props: ForgotPasswordProps) {
        super(props);

        Constants.SetDocumentTitle("Forgot Password");

        this.props.toggleLogonFailure(false);
        this.state = {
            email: "",
            errorMessages: []
        };
    }

    public render() {
        if (this.props.user) {
            RedirectTo(
                Constants.Routes.AuctionList({
                    baseUrl: this.props.salesPageUrl
                })
            );
        }

        const classes = this.props.classes;

        let errorMessages = this.state.errorMessages;
        if (this.props.forgotPasswordFailure) {
            errorMessages = this.props.forgotPasswordFailErrors;
        }

        if (this.props.forgotPasswordSucceed) {
            return (
                <Paper className={classes.paper} elevation={2}>
                    <SectionBanner leftSideText="Password Recovery" />
                    <div className={classes.form}>
                        <form>
                            <SnackbarContent
                                className={classes.successContainer}
                                message={
                                    <span
                                        id="client-snackbar"
                                        className={classes.successMessage}
                                    >
                                        <Info className={classes.successIcon} />
                                        <div>
                                            Please check your Email for the
                                            confirmation link.
                                        </div>
                                    </span>
                                }
                            />
                        </form>
                    </div>
                </Paper>
            );
        }

        return (
            <Paper className={classes.paper} elevation={2}>
                <SectionBanner leftSideText="Password Recovery" />
                <div className={classes.form}>
                    <form>
                        {errorMessages.length > 0
                            ? this.renderFailure(errorMessages)
                            : null}
                        <Typography
                            component="p"
                            variant="body2"
                            className={classes.bodyText}
                        >
                            Please enter your email address. You will receive a
                            link to create a new password via email.
                        </Typography>
                        <TextField
                            className={classes.formFieldTop}
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="Email"
                            name="email"
                            autoFocus={true}
                            value={this.state.email}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                        <div className={classes.button}>
                            <LoaderButton
                                loading={
                                    this.props.authLoadState ===
                                    LoadState.Loading
                                }
                                type="submit"
                                size="large"
                                variant="contained"
                                onClick={this.onSubmit}
                            >
                                Get New Password
                            </LoaderButton>
                        </div>
                    </form>
                </div>
            </Paper>
        );
    }

    private renderFailure = (errors: string[]) => {
        window.scrollTo(0, 0);
        const classes = this.props.classes;

        return (
            <div>
                <Typography align="center" variant="h6" color="error">
                    The following errors occurred
                </Typography>
                <Typography align="center" variant="subtitle1" color="error">
                    <ul>
                        {errors.map((e, idx) => (
                            <li className={classes.errorMessage} key={idx}>
                                {e}
                            </li>
                        ))}
                    </ul>
                </Typography>
            </div>
        );
    };

    private onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value as string;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.setState({ [name]: value } as any);
    };

    private validateForm = () => {
        const errorMessages: string[] = [];

        if (this.state.email.trim().length === 0) {
            errorMessages.push("Email field is required.");
        } else if (!this.state.email.trim().match(Constants.EmailRegExp)) {
            errorMessages.push("Invalid Email Address");
        }

        const hasErrors = errorMessages.length > 0;
        this.setState({ errorMessages });

        if (hasErrors) {
            ReactGA.event(Constants.GAEvents.Form.Invalid);
            AppInsightsLogger.trackException({
                exception: new Error("Forgot Password Validation Error"),
                properties: { errors: errorMessages }
            });
        }

        return errorMessages.length === 0;
    };

    private onSubmit = (e: React.MouseEvent) => {
        e.preventDefault();
        if (this.validateForm()) {
            ReactGA.event(Constants.GAEvents.Form.Submit);
            this.props.forgotPassword(this.state.email);
        }
    };
}

const reduxState = (state: IAppState) => ({
    ...state.userStoreState,
    ...state.appConfigStoreState
});
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(UserStore.actionCreators, dispatch);

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ForgotPassword)
);
